import type { ComponentType } from 'react';
import {
  Analytics01Icon,
  AlertCircleIcon,
  ArrowDown01Icon,
  ArrowLeft01Icon,
  ArrowRight01Icon,
  ArrowUp01Icon,
  AtSignIcon,
  BadgeCheckIcon,
  BellIcon,
  BellOffIcon,
  BotIcon,
  BrainIcon,
  BracesIcon,
  BriefcaseBusinessIcon,
  BulbIcon,
  BubbleChatIcon,
  Building03Icon,
  Cancel01Icon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  CircleIcon,
  Copy01Icon,
  CreditCardIcon,
  Delete02Icon,
  Download01Icon,
  DragDropVerticalIcon,
  Edit02Icon,
  GridViewIcon,
  EyeIcon,
  ExternalLinkIcon,
  File01Icon,
  Flag01Icon,
  FloppyDiskIcon,
  Globe02Icon,
  GraduationCapIcon,
  HeartHandshakeIcon,
  InformationCircleIcon,
  JobSearchIcon,
  Key01Icon,
  LockIcon,
  Logout01Icon,
  Mail01Icon,
  MailSend01Icon,
  Megaphone01Icon,
  Mic01Icon,
  Loading03Icon,
  PlusSignIcon,
  PreferenceVerticalIcon,
  RefreshIcon,
  SearchIcon,
  SlidersHorizontalIcon,
  SecurityCheckIcon,
  SparklesIcon,
  StarIcon,
  Sun03Icon,
  Tick02Icon,
  Upload01Icon,
  Unlink01Icon,
  UserCircleIcon,
  WalletCardsIcon,
} from '@hugeicons/core-free-icons';
import { HugeIcon, type HugeIconData, type HugeIconProps } from './HugeIcon';

export type CompatibleHugeIconProps = Omit<
  HugeIconProps,
  'icon' | 'decorative' | 'size' | 'aria-hidden'
> & {
  size?: number;
  absoluteStrokeWidth?: boolean;
  accessibilityLabel?: string;
  'aria-hidden'?: boolean | 'true' | 'false';
};

export type HugeIconComponent = ComponentType<CompatibleHugeIconProps>;
export type LucideIcon = HugeIconComponent;

function createHugeIcon(icon: HugeIconData): HugeIconComponent {
  return function SemanticHugeIcon({
    accessibilityLabel,
    absoluteStrokeWidth: _absoluteStrokeWidth,
    'aria-hidden': ariaHidden,
    ...props
  }: CompatibleHugeIconProps) {
    return (
      <HugeIcon
        icon={icon}
        decorative={
          ariaHidden === true || ariaHidden === 'true' || !accessibilityLabel
        }
        accessibilityLabel={accessibilityLabel}
        {...props}
      />
    );
  };
}

export const ArrowDown = createHugeIcon(ArrowDown01Icon);
export const ArrowUp = createHugeIcon(ArrowUp01Icon);
export const ArrowLeft = createHugeIcon(ArrowLeft01Icon);
export const ArrowRight = createHugeIcon(ArrowRight01Icon);
export const ChevronDown = ArrowDown;
export const ChevronUp = ArrowUp;
export const ChevronLeft = ArrowLeft;
export const ChevronRight = ArrowRight;
export const AtSign = createHugeIcon(AtSignIcon);
export const Bell = createHugeIcon(BellIcon);
export const BellOff = createHugeIcon(BellOffIcon);
export const Bot = createHugeIcon(BotIcon);
export const Brain = createHugeIcon(BrainIcon);
export const Braces = createHugeIcon(BracesIcon);
export const Briefcase = createHugeIcon(JobSearchIcon);
export const BriefcaseBusiness = createHugeIcon(BriefcaseBusinessIcon);
export const Building2 = createHugeIcon(Building03Icon);
export const Check = createHugeIcon(Tick02Icon);
export const CheckCircle = createHugeIcon(CheckmarkCircle02Icon);
export const CircleCheck = CheckCircle;
export const Circle = createHugeIcon(CircleIcon);
export const CircleAlert = createHugeIcon(AlertCircleIcon);
export const Copy = createHugeIcon(Copy01Icon);
export const CreditCard = createHugeIcon(CreditCardIcon);
export const Download = createHugeIcon(Download01Icon);
export const Edit = createHugeIcon(Edit02Icon);
export const Pencil = Edit;
export const Eye = createHugeIcon(EyeIcon);
export const ExternalLink = createHugeIcon(ExternalLinkIcon);
export const FileText = createHugeIcon(File01Icon);
export const LayoutGrid = createHugeIcon(GridViewIcon);
export const Lightbulb = createHugeIcon(BulbIcon);
export const Flag = createHugeIcon(Flag01Icon);
export const Globe = createHugeIcon(Globe02Icon);
export const Globe2 = Globe;
export const GraduationCap = createHugeIcon(GraduationCapIcon);
export const GripVertical = createHugeIcon(DragDropVerticalIcon);
export const HeartHandshake = createHugeIcon(HeartHandshakeIcon);
export const HelpCircle = createHugeIcon(InformationCircleIcon);
export const Info = HelpCircle;
export const KeyRound = createHugeIcon(Key01Icon);
export const Link2Off = createHugeIcon(Unlink01Icon);
export const Lock = createHugeIcon(LockIcon);
export const LogOut = createHugeIcon(Logout01Icon);
export const Mail = createHugeIcon(Mail01Icon);
export const MailSend = createHugeIcon(MailSend01Icon);
export const Megaphone = createHugeIcon(Megaphone01Icon);
export const MessageSquare = createHugeIcon(BubbleChatIcon);
export const MessageCircle = MessageSquare;
export const Mic = createHugeIcon(Mic01Icon);
export const Plus = createHugeIcon(PlusSignIcon);
export const Search = createHugeIcon(SearchIcon);
export const Save = createHugeIcon(FloppyDiskIcon);
export const Loader2 = createHugeIcon(Loading03Icon);
export const RotateCcw = createHugeIcon(RefreshIcon);
export const RotateCw = RotateCcw;
export const ShieldCheck = createHugeIcon(SecurityCheckIcon);
export const Sliders = createHugeIcon(PreferenceVerticalIcon);
export const SlidersHorizontal = createHugeIcon(SlidersHorizontalIcon);
export const Sparkles = createHugeIcon(SparklesIcon);
export const Star = createHugeIcon(StarIcon);
export const SunMoon = createHugeIcon(Sun03Icon);
export const Trash2 = createHugeIcon(Delete02Icon);
export const Upload = createHugeIcon(Upload01Icon);
export const UserRound = createHugeIcon(UserCircleIcon);
export const WalletCards = createHugeIcon(WalletCardsIcon);
export const WandSparkles = Sparkles;
export const X = createHugeIcon(Cancel01Icon);
export const XCircle = createHugeIcon(CancelCircleIcon);
export const ArrowUpCircle = createHugeIcon(Upload01Icon);
export const BadgeCheck = createHugeIcon(BadgeCheckIcon);
export const BarChart3 = createHugeIcon(Analytics01Icon);
